<template lang="pug">
div(class="tl-input-phone-number-wrapper")
    InputText(
        class="tl-input-phone-number"
        :model-value="modelValue"
        :label="label"
        :error="error || isPhoneValid ? '' : 'Phone number is incorrect'"
        :focus="focus"
        :disabled="disabled"
        :options="{ type: 'tel' }"
        :dense="dense"
        @mounted="initTelInput"
        @update:model-value="handleUpdate()"
        @focus="focused = true"
        @blur="focused = false"
    )
    div(
        ref="dropdownContainer"
        class="dropdown-container"
    )
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import InputText from './InputText.vue'

import intlTelInput from 'intl-tel-input'
import type { InputPhoneNumberDetails } from '@/types/components'

const props = defineProps({
    defaultValue: String,
    modelValue: String,
    label: String,
    disabled: Boolean,
    focus: Boolean,
    dense: Boolean,
    error: {
        type: [String, Boolean],
        default: undefined,
    },
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
    (e: 'update:details', value: InputPhoneNumberDetails): void
}>()

const input = ref<HTMLInputElement | null>(null)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const telInput = ref<any>(null)
const dropdownContainer = ref(null)

const focused = ref(false)
const label = computed(() => props.label)

const isPhoneValid = ref(true)

const getPhoneDetails = (val?: string) => {
    const fullNumber = val ?? telInput.value.getNumber()
    const countryDetails = telInput.value.getSelectedCountryData()
    const prefix = countryDetails.dialCode ? `+${countryDetails.dialCode}` : ''
    const countryName = countryDetails.name
    const countryCode = countryDetails.iso2
    const phoneNumber = fullNumber
        .replace(prefix, '')
        .replace('+', '')
    const isValid = fullNumber.length
        ? telInput.value.isValidNumber()
        : true

    return {
        phoneNumber,
        fullNumber,
        prefix,
        countryName,
        countryCode,
        isValid,
    }
}

const handleUpdate = (val?: string) => {
    const details = getPhoneDetails(val)
    const { fullNumber, isValid: valid } = details

    isPhoneValid.value = valid

    if (fullNumber !== props.modelValue) {
        emit('update:modelValue', fullNumber)
        emit('update:details', details)
    }
}

const handleCountryChange = async (event: Event) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    handleUpdate(event?.target?.value)
}

const initTelInput = async (inputElem: HTMLInputElement) => {
    input.value = inputElem

    telInput.value = intlTelInput(inputElem, {
        dropdownContainer: dropdownContainer.value!,
        loadUtils: () => import('intl-tel-input/utils'),
        countrySearch: true,
    })

    input.value.addEventListener('countrychange', handleCountryChange)

    await telInput.value.promise

    handleUpdate()
}

onMounted(async () => {
    if (props.focus && input.value) {
        // await wait(800)
        input.value.focus()
    }

    if (props.defaultValue) {
        emit('update:modelValue', props.defaultValue)
    }
})

onBeforeUnmount(() => {
    if (input.value) {
        input.value.removeEventListener('countrychange', handleCountryChange)
    }

    if (telInput.value) {
        telInput.value.destroy()
    }
})
</script>

<style scoped lang="sass">
.tl-input-phone-number-wrapper
    :deep(.tl-input-text:not(.focused))
        input[data-intl-tel-input-id]::placeholder
            color: transparent

    :deep(.label)
        left: var(--tl-input-phone-number-offset) !important

    :deep(.iti--container)
        position: relative !important
        top: -14px !important
        left: 0 !important

    :deep(.iti__search-input)
        height: 32px

    .tl-input-phone-number
        :deep(.iti__selected-country-primary:hover)
            background-color: transparent!important
        &.dense
            --tl-input-phone-number-offset: 50px
            :deep(.iti__flag-container)
                left: -5px
</style>
